import { CloseOutlined, InboxOutlined, MinusOutlined } from '@vindr/icons';
import { uuidv4 } from '@vindr/utils/helpers/common.helper';
import { isMobile } from '@vindr/utils/mobile';
import { Button, Checkbox, Radio, RadioChangeEvent, Typography } from 'antd';
import { concat } from 'lodash';
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteList } from './ScrollRender';
import FileRow from './components/FileRow';
import UploadForm from './components/FormUpload';
import ProcessBar from './components/ProcessBar';
import { DicomMode, UploadExpandProps } from './context/type';
import useSetDragFile from './hooks/useSetDragFile';

const { Title } = Typography;

const dicomAcceptType = '.dcm, .dicom';
// const acceptTypeReg = new RegExp(`${dicomAcceptType.replace(', ', '|')}$`, 'i');

const UploadExpand = ({
  appearance,
  acceptType = dicomAcceptType,
  dispatch,
  failItem,
  fileList,
  form,
  handleClose,
  isFinish,
  isMinimize,
  onUpload,
  processingList,
  remainingCount,
  onCancel,
  title = 'Upload Files',
  uploading,
  params,
  message,
}: UploadExpandProps) => {
  const [inputFile, inputFolder] = [
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
  ];
  const [showError, setShowError] = useState(false);
  const [dicomMode, setDicomMode] = useState<DicomMode>(
    appearance === 'dicom' ? 'dicom' : 'attachment'
  );

  return (
    <section
      className={`h-1/2 top-1/2 transform rounded-sm bg-primary-lighter p-6 text-black shadow-lg dark:bg-primary-600 dark:text-white sm:left-0 sm:w-full md:inset-0 md:left-1/2 md:w-2/4 md:translate-x-1/2 ${
        isMinimize ? 'hidden' : ''
      }`}
    >
      <HeaderMemo
        {...{ dispatch, isMinimize, handleClose, title, dicomMode, setDicomMode, appearance }}
      />
      <UploadForm {...{ dicomMode, dispatch, fileList, form, params, message }} />
      <BodyMemo
        {...{
          acceptType,
          dispatch,
          failItem,
          fileList,
          inputFile,
          inputFolder,
          isFinish,
          processingList,
          remainingCount,
          showError,
          uploading,
        }}
      />
      <FooterMemo
        {...{
          dispatch,
          failItem,
          fileList,
          form,
          inputFile,
          inputFolder,
          isFinish,
          onUpload,
          onCancel,
          setShowError,
          uploading,
          dicomMode,
        }}
      />
    </section>
  );
};

const Body = ({
  acceptType,
  dispatch,
  failItem,
  fileList,
  inputFile,
  inputFolder,
  isFinish,
  processingList,
  remainingCount,
  showError,
  uploading,
}: {
  acceptType: UploadExpandProps['acceptType'];
  dispatch: UploadExpandProps['dispatch'];
  failItem: UploadExpandProps['failItem'];
  fileList: UploadExpandProps['fileList'];
  inputFile: MutableRefObject<HTMLInputElement | null>;
  inputFolder: MutableRefObject<HTMLInputElement | null>;
  isFinish: UploadExpandProps['isFinish'];
  processingList: UploadExpandProps['processingList'];
  remainingCount: UploadExpandProps['remainingCount'];
  showError: boolean;
  uploading: UploadExpandProps['uploading'];
}) => {
  const dragBox = useRef(null);
  const { t } = useTranslation('Vindoc');

  const onChangeFile = (event: ChangeEvent<any>, isSelectFile: boolean) => {
    if (!event?.target?.files) return;
    addFiles(event.target.files, isSelectFile);
  };

  const addFiles = useCallback(
    (files = [], isSelectFile: boolean) => {
      const filesTemp = [];
      for (const file of files) {
        // if (!acceptTypeReg.test(file.name)) continue;

        filesTemp.push({
          fileId: uuidv4(),
          name: file.name,
          originFileObj: file,
          progress: 0,
          size: file.size,
          status: 'ready',
        });
      }

      dispatch({
        type: 'setFileList',
        payload: isSelectFile ? concat(fileList, filesTemp) : filesTemp,
      });
    },
    [dispatch, fileList]
  );

  const cleanFileList = (event: any) => {
    event.target.value = '';
  };

  const handleDeleteFile = useCallback(
    (fileProps) => {
      if (uploading) return;
      dispatch({
        type: 'setDeleteFile',
        payload: fileProps.fileId,
      });
    },
    [fileList, uploading]
  );

  useSetDragFile(dragBox, addFiles);

  return (
    <section>
      <div className="upload-container">
        <input
          className="input-folder hidden"
          type="file"
          multiple
          ref={(node) => {
            inputFolder.current = node;

            if (node) {
              ['webkitdirectory', 'directory', 'mozdirectory'].forEach((attr) => {
                node.setAttribute(attr, '');
              });
            }
          }}
          onChange={(e) => {
            onChangeFile(e, true);
          }}
          onClick={cleanFileList}
        />
        <input
          className="input-file hidden"
          type="file"
          multiple
          ref={inputFile}
          accept={acceptType}
          onChange={(e) => {
            onChangeFile(e, true);
          }}
          onClick={cleanFileList}
        />

        {fileList.length === 0 && (
          <div
            className="drag-box flex h-96 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-600"
            onClick={() => inputFile.current.click()}
            ref={dragBox}
          >
            <InboxOutlined className="fill-[#000] text-4xl dark:fill-[#fff]" />
            <p className="mt-2 text-center">
              {t('Drag and drop files here or')}{' '}
              <span className="text-blue-500">{t('browse')}</span>
            </p>
          </div>
        )}

        {fileList.length > 0 && (
          <div className="file-list">
            <ProcessBar {...{ processingList, uploading, isFinish, remainingCount, failItem }} />
            <div className="list h-96 w-full overflow-auto text-base">
              {showError ? (
                <InfiniteList list={failItem}>
                  {(item) => {
                    const file = processingList.get(item?.id);
                    return (
                      <FileRow
                        key={item?.id}
                        file={file}
                        handleDeleteFile={handleDeleteFile}
                        uploading={uploading}
                      />
                    );
                  }}
                </InfiniteList>
              ) : (
                <InfiniteList list={Array.from(processingList.values())}>
                  {(item) => (
                    <FileRow
                      key={item?.fileId}
                      file={item}
                      handleDeleteFile={handleDeleteFile}
                      uploading={uploading}
                    />
                  )}
                </InfiniteList>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
const BodyMemo = memo(Body);

const Header = ({
  appearance,
  dicomMode,
  dispatch,
  handleClose,
  isMinimize,
  setDicomMode,
  title,
}: {
  appearance: UploadExpandProps['appearance'];
  dicomMode: DicomMode;
  dispatch: UploadExpandProps['dispatch'];
  handleClose: UploadExpandProps['handleClose'];
  isMinimize: UploadExpandProps['isMinimize'];
  setDicomMode: Dispatch<SetStateAction<DicomMode>>;
  title: UploadExpandProps['title'];
}) => {
  const { t } = useTranslation('Vindoc');

  const onChange = (e: RadioChangeEvent) => {
    setDicomMode(e.target.value);
  };

  return (
    <header className="flex items-start justify-between border-b border-gray-800 pb-3">
      <div className="flex flex-wrap items-center space-x-3 self-end">
        <Title level={3} className="mb-0">
          {t(title)}
        </Title>
        {appearance === 'dicom' && (
          <Radio.Group onChange={onChange} value={dicomMode}>
            <Radio value={'dicom'}>{t('DICOM')}</Radio>
            {/* <Radio value={'non-dicom'}>{t('NON-DICOM')}</Radio> */}
          </Radio.Group>
        )}
        {appearance === 'attachment' && (
          <Radio.Group onChange={onChange} value={dicomMode}>
            <Radio value={'attachment'}>{t('File Attachment')}</Radio>
            <Radio value={'pathology'}>{t('Pathology')}</Radio>
          </Radio.Group>
        )}
      </div>

      <div className="flex">
        <Button
          className="mr-2 w-[20px] fill-[#000] p-0 dark:fill-[#fff]"
          type="text"
          size="small"
          onClick={() => dispatch({ type: 'setIsMinimize', payload: !isMinimize })}
        >
          <MinusOutlined className="text-lg leading-4" />
        </Button>
        <Button
          type="text"
          className=" w-[20px] fill-[#000] p-0 dark:fill-[#fff]"
          size="small"
          onClick={handleClose}
        >
          <CloseOutlined className="text-lg leading-4" />
        </Button>
      </div>
    </header>
  );
};
const HeaderMemo = memo(Header);

const Footer = ({
  dispatch,
  failItem,
  fileList,
  inputFile,
  inputFolder,
  isFinish,
  onUpload,
  onCancel,
  setShowError,
  uploading,
  dicomMode,
}: {
  dispatch: UploadExpandProps['dispatch'];
  failItem: UploadExpandProps['failItem'];
  fileList: UploadExpandProps['fileList'];
  inputFile: MutableRefObject<HTMLInputElement | null>;
  inputFolder: MutableRefObject<HTMLInputElement | null>;
  isFinish: UploadExpandProps['isFinish'];
  onUpload: UploadExpandProps['onUpload'];
  dicomMode: DicomMode;
  onCancel: UploadExpandProps['onCancel'];
  setShowError: Dispatch<SetStateAction<boolean>>;
  uploading: UploadExpandProps['uploading'];
}) => {
  const { t } = useTranslation('Vindoc');

  const handleRetry = useCallback(() => {
    const files = failItem.map((item) => {
      const file = fileList.find((file) => file.fileId === item.id);

      return { ...file, status: 'ready', progress: 0 };
    });

    dispatch({ type: 'retry', payload: files });

    setShowError(false);
  }, [failItem]);

  return (
    <footer>
      {isFinish && failItem.length > 0 && (
        <div className="mt-4 text-center">
          <Checkbox
            onChange={(e) => {
              setShowError(e.target.checked);
            }}
          >
            {t('Only show failed files')}
          </Checkbox>
        </div>
      )}
      <section className="align-center mt-6 flex flex-wrap justify-between gap-4">
        <div className="flex flex-wrap items-baseline space-y-2">
          {isFinish || uploading ? null : (
            <>
              <Button
                onClick={() => {
                  inputFile.current.click();
                }}
                className="mr-2 "
              >
                {t('Select Files')}
              </Button>
              {!isMobile() && (
                <Button onClick={() => inputFolder.current.click()}>{t('Select Folder')}</Button>
              )}
            </>
          )}

          {isFinish && failItem.length > 0 && (
            <Button onClick={handleRetry} disabled={failItem.length === 0}>
              {t('Retry')}
            </Button>
          )}
        </div>
        <Button
          onClick={() => {
            if (isFinish) {
              onCancel();
            } else {
              onUpload(dicomMode);
            }
          }}
          loading={uploading}
          disabled={fileList.length === 0}
        >
          {isFinish ? t('Close') : t('Upload')}
        </Button>
      </section>
    </footer>
  );
};
const FooterMemo = memo(Footer);

export default memo(UploadExpand);
